/**
 * User Documentation Manager (Superadmin)
 * - List flows
 * - Edit flow (title, description, notes, steps with screenshot upload)
 * - Add/remove/reorder steps
 * - Download PDF
 */
import React, { useEffect, useState, useRef } from 'react';
import DOMPurify from 'dompurify';
import axios from 'axios';
import { toast } from 'sonner';
import {
    LifeBuoy, Plus, Trash2, Edit, Image as ImageIcon, Download,
    ChevronUp, ChevronDown, Save, X, Loader2, FileText, Eye,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent } from '../../components/ui/card';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

function authHeaders() {
    return { Authorization: `Bearer ${localStorage.getItem('superadmin_token')}` };
}

async function fileToDataUrl(file) {
    return new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = reject;
        r.readAsDataURL(file);
    });
}

export default function UserDocsManager() {
    const [flows, setFlows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [downloadingPdf, setDownloadingPdf] = useState(false);
    const [activeFlow, setActiveFlow] = useState(null);
    const [createOpen, setCreateOpen] = useState(false);
    const [createForm, setCreateForm] = useState({ flow_key: '', title: '', description: '', notes: '' });

    const fetchFlows = async () => {
        try {
            const res = await axios.get(`${API_BASE}/api/superadmin/user-docs`, { headers: authHeaders() });
            setFlows(res.data || []);
        } catch (err) {
            toast.error('Gagal memuat flows');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchFlows(); }, []);

    const handleDownloadPdf = async () => {
        setDownloadingPdf(true);
        try {
            const res = await axios.get(`${API_BASE}/api/superadmin/user-docs/pdf/download`, {
                headers: authHeaders(),
                responseType: 'blob',
            });
            const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
            const a = document.createElement('a');
            a.href = url;
            a.download = `TaskGlory-UserDocs-${new Date().toISOString().slice(0, 10)}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success('PDF didownload');
        } catch (err) {
            toast.error('Gagal generate PDF');
        } finally {
            setDownloadingPdf(false);
        }
    };

    const handlePreviewPdf = async () => {
        try {
            const res = await axios.get(`${API_BASE}/api/superadmin/user-docs/pdf/download`, {
                headers: authHeaders(),
                responseType: 'blob',
            });
            const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
            window.open(url, '_blank');
        } catch (err) {
            toast.error('Gagal preview PDF');
        }
    };

    const handleCreate = async () => {
        if (!createForm.flow_key || !createForm.title) {
            toast.error('flow_key dan title wajib diisi');
            return;
        }
        try {
            await axios.post(`${API_BASE}/api/superadmin/user-docs`, createForm, { headers: authHeaders() });
            toast.success('Flow dibuat');
            setCreateOpen(false);
            setCreateForm({ flow_key: '', title: '', description: '', notes: '' });
            fetchFlows();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Gagal membuat flow');
        }
    };

    const handleDelete = async (id, title) => {
        if (!window.confirm(`Hapus flow "${title}"?`)) return;
        try {
            await axios.delete(`${API_BASE}/api/superadmin/user-docs/${id}`, { headers: authHeaders() });
            toast.success('Flow dihapus');
            fetchFlows();
            if (activeFlow?.id === id) setActiveFlow(null);
        } catch (err) {
            toast.error('Gagal menghapus flow');
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64 text-slate-300">
                <Loader2 className="w-6 h-6 animate-spin mr-2" /> Memuat...
            </div>
        );
    }

    return (
        <div className="p-6 space-y-6" data-testid="user-docs-manager">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                        <LifeBuoy className="w-6 h-6 text-amber-500" />
                        User Documentation
                    </h1>
                    <p className="text-slate-400 text-sm mt-1">
                        Dokumentasi cara penggunaan TaskGlory untuk end user. Edit konten dan download sebagai PDF.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" onClick={handlePreviewPdf}
                        className="bg-slate-800 border-slate-600 text-slate-200 hover:bg-slate-700"
                        data-testid="preview-pdf-btn">
                        <Eye className="w-4 h-4 mr-1" /> Preview PDF
                    </Button>
                    <Button onClick={handleDownloadPdf} disabled={downloadingPdf}
                        className="bg-amber-600 hover:bg-amber-700 text-white"
                        data-testid="download-pdf-btn">
                        {downloadingPdf
                            ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                            : <Download className="w-4 h-4 mr-1" />}
                        Download PDF
                    </Button>
                </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4">
                <StatCard
                    label="Total Flows"
                    value={flows.length}
                    icon={FileText}
                    color="text-blue-400"
                />
                <StatCard
                    label="Total Steps"
                    value={flows.reduce((acc, f) => acc + (f.step_count || 0), 0)}
                    icon={Edit}
                    color="text-emerald-400"
                />
                <StatCard
                    label="Steps with Screenshot"
                    value={flows.reduce((acc, f) => acc + (f.screenshot_count || 0), 0)}
                    icon={ImageIcon}
                    color="text-amber-400"
                />
            </div>

            {/* Flow list + editor */}
            <div className="grid grid-cols-12 gap-6">
                {/* List */}
                <Card className="col-span-4 bg-slate-800 border-slate-700">
                    <CardContent className="p-4 space-y-2">
                        <div className="flex items-center justify-between mb-2">
                            <h2 className="text-white font-semibold text-sm">Flows ({flows.length})</h2>
                            <Button size="sm" onClick={() => setCreateOpen(true)}
                                className="bg-emerald-600 hover:bg-emerald-700 text-white h-7"
                                data-testid="create-flow-btn">
                                <Plus className="w-3.5 h-3.5 mr-1" /> Tambah
                            </Button>
                        </div>
                        {flows.length === 0 && (
                            <div className="text-slate-400 text-sm p-4 text-center">Belum ada flow.</div>
                        )}
                        {flows.map(f => (
                            <div key={f.id}
                                className={`p-3 rounded border cursor-pointer transition-colors ${
                                    activeFlow?.id === f.id
                                        ? 'bg-slate-700 border-amber-500'
                                        : 'bg-slate-900 border-slate-700 hover:bg-slate-700/50'
                                }`}
                                onClick={() => setActiveFlow({ id: f.id })}
                                data-testid={`flow-row-${f.flow_key}`}>
                                <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0 flex-1">
                                        <div className="text-white font-medium text-sm truncate">{f.title}</div>
                                        <div className="text-slate-500 text-xs mt-0.5 font-mono">{f.flow_key}</div>
                                        <div className="flex items-center gap-2 mt-1.5 text-xs text-slate-400">
                                            <span>{f.step_count} steps</span>
                                            <span>·</span>
                                            <span className="flex items-center gap-1">
                                                <ImageIcon className="w-3 h-3" /> {f.screenshot_count}
                                            </span>
                                        </div>
                                    </div>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); handleDelete(f.id, f.title); }}
                                        className="text-red-400 hover:text-red-300 p-1"
                                        data-testid={`delete-flow-${f.flow_key}`}>
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </CardContent>
                </Card>

                {/* Editor */}
                <div className="col-span-8">
                    {activeFlow
                        ? <FlowEditor flowId={activeFlow.id} onChange={fetchFlows} />
                        : (
                            <Card className="bg-slate-800 border-slate-700">
                                <CardContent className="p-12 text-center">
                                    <Edit className="w-10 h-10 mx-auto text-slate-500 mb-3" />
                                    <p className="text-slate-400">Pilih salah satu flow di list kiri untuk mengedit.</p>
                                </CardContent>
                            </Card>
                        )}
                </div>
            </div>

            {/* Create flow modal */}
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                <DialogContent className="bg-slate-800 text-slate-200 border-slate-700">
                    <DialogHeader>
                        <DialogTitle>Tambah Flow Baru</DialogTitle>
                        <DialogDescription className="text-slate-400">
                            Buat flow baru untuk dokumentasi user.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label className="text-xs">Flow Key (unique, snake_case)</Label>
                            <Input value={createForm.flow_key}
                                onChange={(e) => setCreateForm(p => ({ ...p, flow_key: e.target.value.toLowerCase().replace(/\s+/g, '_') }))}
                                placeholder="contoh: invite_member"
                                className="bg-slate-900 border-slate-700 mt-1"
                                data-testid="create-flow-key" />
                        </div>
                        <div>
                            <Label className="text-xs">Title</Label>
                            <Input value={createForm.title}
                                onChange={(e) => setCreateForm(p => ({ ...p, title: e.target.value }))}
                                placeholder="contoh: Mengundang Member ke Workspace"
                                className="bg-slate-900 border-slate-700 mt-1"
                                data-testid="create-flow-title" />
                        </div>
                        <div>
                            <Label className="text-xs">Description (opsional)</Label>
                            <Textarea value={createForm.description}
                                onChange={(e) => setCreateForm(p => ({ ...p, description: e.target.value }))}
                                rows={3}
                                className="bg-slate-900 border-slate-700 mt-1" />
                        </div>
                        <div>
                            <Label className="text-xs">Notes / Tips (opsional)</Label>
                            <Textarea value={createForm.notes}
                                onChange={(e) => setCreateForm(p => ({ ...p, notes: e.target.value }))}
                                rows={2}
                                className="bg-slate-900 border-slate-700 mt-1" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setCreateOpen(false)}
                            className="text-slate-300 hover:bg-slate-700">Batal</Button>
                        <Button onClick={handleCreate}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white"
                            data-testid="create-flow-submit">
                            <Save className="w-4 h-4 mr-1" /> Buat
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function StatCard({ label, value, icon: Icon, color }) {
    return (
        <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-4 flex items-center gap-3">
                <Icon className={`w-7 h-7 ${color}`} />
                <div>
                    <div className="text-2xl font-bold text-white">{value}</div>
                    <div className="text-xs text-slate-400">{label}</div>
                </div>
            </CardContent>
        </Card>
    );
}

function FlowEditor({ flowId, onChange }) {
    const [flow, setFlow] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [meta, setMeta] = useState({ title: '', description: '', notes: '' });
    const [stepDraft, setStepDraft] = useState('');
    const [draftFile, setDraftFile] = useState(null);
    const fileRef = useRef(null);

    const fetchFlow = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API_BASE}/api/superadmin/user-docs/${flowId}`, { headers: authHeaders() });
            setFlow(res.data);
            setMeta({
                title: res.data.title || '',
                description: res.data.description || '',
                notes: res.data.notes || '',
            });
        } catch (err) {
            toast.error('Gagal memuat flow');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchFlow(); }, [flowId]);

    const saveMeta = async () => {
        setSaving(true);
        try {
            await axios.put(`${API_BASE}/api/superadmin/user-docs/${flowId}`, meta, { headers: authHeaders() });
            toast.success('Disimpan');
            fetchFlow();
            onChange?.();
        } catch (err) {
            toast.error('Gagal menyimpan');
        } finally {
            setSaving(false);
        }
    };

    const addStep = async () => {
        if (!stepDraft.trim()) {
            toast.error('Deskripsi step wajib diisi');
            return;
        }
        try {
            const payload = { description: stepDraft.trim() };
            if (draftFile) {
                payload.screenshot_b64 = await fileToDataUrl(draftFile);
            }
            await axios.post(`${API_BASE}/api/superadmin/user-docs/${flowId}/steps`, payload, { headers: authHeaders() });
            toast.success('Step ditambahkan');
            setStepDraft('');
            setDraftFile(null);
            if (fileRef.current) fileRef.current.value = '';
            fetchFlow();
            onChange?.();
        } catch (err) {
            toast.error('Gagal tambah step');
        }
    };

    const updateStepImage = async (stepId, file, currentDescription) => {
        try {
            const dataUrl = await fileToDataUrl(file);
            await axios.put(`${API_BASE}/api/superadmin/user-docs/${flowId}/steps/${stepId}`,
                { description: currentDescription, screenshot_b64: dataUrl },
                { headers: authHeaders() });
            toast.success('Screenshot diupdate');
            fetchFlow();
            onChange?.();
        } catch (err) {
            toast.error('Gagal update screenshot');
        }
    };

    const updateStepDescription = async (stepId, newDescription) => {
        try {
            await axios.put(`${API_BASE}/api/superadmin/user-docs/${flowId}/steps/${stepId}`,
                { description: newDescription },
                { headers: authHeaders() });
            toast.success('Step diupdate');
            fetchFlow();
        } catch (err) {
            toast.error('Gagal update step');
        }
    };

    const deleteStep = async (stepId) => {
        if (!window.confirm('Hapus step ini?')) return;
        try {
            await axios.delete(`${API_BASE}/api/superadmin/user-docs/${flowId}/steps/${stepId}`, { headers: authHeaders() });
            toast.success('Step dihapus');
            fetchFlow();
            onChange?.();
        } catch (err) {
            toast.error('Gagal hapus step');
        }
    };

    if (loading) return (
        <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-12 flex items-center justify-center text-slate-300">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Memuat...
            </CardContent>
        </Card>
    );

    if (!flow) return null;

    const sortedSteps = [...(flow.steps || [])].sort((a, b) => (a.order || 0) - (b.order || 0));

    return (
        <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-5 space-y-5">
                {/* Meta */}
                <div className="space-y-3">
                    <div className="flex items-center justify-between">
                        <h2 className="text-white font-semibold">Edit Flow</h2>
                        <span className="text-xs text-slate-500 font-mono">{flow.flow_key}</span>
                    </div>
                    <div>
                        <Label className="text-xs">Title</Label>
                        <Input value={meta.title}
                            onChange={(e) => setMeta(p => ({ ...p, title: e.target.value }))}
                            className="bg-slate-900 border-slate-700 mt-1"
                            data-testid="flow-edit-title" />
                    </div>
                    <div>
                        <Label className="text-xs">Description</Label>
                        <Textarea value={meta.description}
                            onChange={(e) => setMeta(p => ({ ...p, description: e.target.value }))}
                            rows={2}
                            className="bg-slate-900 border-slate-700 mt-1" />
                    </div>
                    <div>
                        <Label className="text-xs">Notes / Tips</Label>
                        <Textarea value={meta.notes}
                            onChange={(e) => setMeta(p => ({ ...p, notes: e.target.value }))}
                            rows={2}
                            className="bg-slate-900 border-slate-700 mt-1" />
                    </div>
                    <Button onClick={saveMeta} disabled={saving}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid="flow-save-meta">
                        {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                        Simpan Meta
                    </Button>
                </div>

                {/* Steps */}
                <div className="border-t border-slate-700 pt-4">
                    <h3 className="text-white font-semibold text-sm mb-3">Steps ({sortedSteps.length})</h3>
                    <div className="space-y-3">
                        {sortedSteps.map((s, idx) => (
                            <StepRow key={s.id}
                                step={s}
                                index={idx}
                                onUpdateDesc={(d) => updateStepDescription(s.id, d)}
                                onUpdateImage={(f) => updateStepImage(s.id, f, s.description)}
                                onDelete={() => deleteStep(s.id)} />
                        ))}
                        {sortedSteps.length === 0 && (
                            <div className="text-slate-500 text-sm">Belum ada step.</div>
                        )}
                    </div>

                    {/* Add new step */}
                    <div className="mt-4 p-3 bg-slate-900 rounded border border-slate-700 space-y-2">
                        <Label className="text-xs">Tambah Step</Label>
                        <Textarea value={stepDraft}
                            onChange={(e) => setStepDraft(e.target.value)}
                            placeholder="Deskripsi step (HTML/markdown sederhana didukung untuk <b>bold</b>)..."
                            rows={2}
                            className="bg-slate-800 border-slate-700"
                            data-testid="step-draft-desc" />
                        <div className="flex items-center gap-2">
                            <input type="file" accept="image/*" ref={fileRef}
                                onChange={(e) => setDraftFile(e.target.files?.[0] || null)}
                                className="text-xs text-slate-400 flex-1"
                                data-testid="step-draft-file" />
                            {draftFile && (
                                <span className="text-xs text-emerald-400">
                                    {draftFile.name} ({Math.round(draftFile.size / 1024)} KB)
                                </span>
                            )}
                            <Button onClick={addStep} size="sm"
                                className="bg-amber-600 hover:bg-amber-700 text-white"
                                data-testid="step-add-btn">
                                <Plus className="w-4 h-4 mr-1" /> Tambah
                            </Button>
                        </div>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}

function StepRow({ step, index, onUpdateDesc, onUpdateImage, onDelete }) {
    const [editing, setEditing] = useState(false);
    const [desc, setDesc] = useState(step.description || '');
    const fileRef = useRef(null);

    const handleFileChange = (e) => {
        const f = e.target.files?.[0];
        if (f) onUpdateImage(f);
    };

    return (
        <div className="p-3 bg-slate-900 rounded border border-slate-700 space-y-2"
            data-testid={`step-row-${step.id}`}>
            <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 text-xs text-slate-400">
                    <span className="px-2 py-0.5 bg-amber-600 text-white rounded font-semibold">Step {index + 1}</span>
                </div>
                <div className="flex items-center gap-1">
                    <button onClick={() => setEditing(v => !v)}
                        className="text-slate-400 hover:text-amber-400 p-1"
                        data-testid={`step-edit-${step.id}`}>
                        <Edit className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={onDelete}
                        className="text-red-400 hover:text-red-300 p-1"
                        data-testid={`step-delete-${step.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                    </button>
                </div>
            </div>
            {editing ? (
                <div className="space-y-2">
                    <Textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={3}
                        className="bg-slate-800 border-slate-700 text-sm" />
                    <div className="flex items-center gap-2">
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white"
                            onClick={() => { onUpdateDesc(desc); setEditing(false); }}>
                            <Save className="w-3.5 h-3.5 mr-1" /> Simpan
                        </Button>
                        <Button size="sm" variant="ghost" className="text-slate-300 hover:bg-slate-700"
                            onClick={() => { setDesc(step.description || ''); setEditing(false); }}>
                            <X className="w-3.5 h-3.5 mr-1" /> Batal
                        </Button>
                    </div>
                </div>
            ) : (
                <div className="text-slate-200 text-sm" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(step.description || '') }} />
            )}

            {/* Screenshot */}
            <div className="border-t border-slate-700 pt-2">
                {step.screenshot_b64 ? (
                    <div className="space-y-1">
                        <img src={step.screenshot_b64} alt="step screenshot"
                            className="max-h-48 rounded border border-slate-600" />
                        <div className="flex items-center gap-2">
                            <input type="file" accept="image/*" ref={fileRef}
                                onChange={handleFileChange} className="text-xs text-slate-400" />
                            <span className="text-xs text-slate-500">Ganti screenshot</span>
                        </div>
                    </div>
                ) : (
                    <div className="flex items-center gap-2">
                        <input type="file" accept="image/*" ref={fileRef}
                            onChange={handleFileChange} className="text-xs text-slate-400" />
                        <span className="text-xs text-slate-500">+ Upload screenshot</span>
                    </div>
                )}
            </div>
        </div>
    );
}
